import type { ImageSource } from "@/libs/image"
import { Graph } from "../../framework"
import { extractFacePixels } from "../../modules/face-cropper/utils"
import {
  create as createBlurNoiseNode,
  type BlurNoiseMetrics,
} from "../../modules/quality/blur-noise"
import {
  create as createResolutionNode,
  type ResolutionMetrics,
} from "../../modules/quality/resolution"
import type { FaceDetection, FramePixels } from "../../modules/types"

export interface QualityMetrics {
  blurScore: number
  noiseScore: number
  resolutionScore: number
  fullFaceVisible: boolean
}

export const create = async () => {
  const graph = new Graph<[ImageSource, FaceDetection | null], [QualityMetrics]>()
  const imageSource = graph.in(0)
  const faceDetection = graph.in(1)

  if (!imageSource || !faceDetection) {
    throw new Error("Quality graph requires both image source and face detection inputs")
  }

  const extractionNode = graph.node<[ImageSource, FaceDetection | null], [FramePixels]>({
    process: async (ctx) => {
      const imageSource = ctx.in(0)
      const faceDetection = ctx.in(1)
      const facePixels = await extractFacePixels(imageSource, faceDetection)
      ctx.out(0, facePixels)
    },
  })

  const blurNoiseNode = await createBlurNoiseNode(graph)

  imageSource.connect(extractionNode.in(0))
  faceDetection.connect(extractionNode.in(1))

  const facePixels = extractionNode.out(0)

  facePixels.connect(blurNoiseNode.in(0))

  // Resolution metrics use the original image and face detection.
  const resolutionNode = await createResolutionNode(graph)
  graph.in(0).connect(resolutionNode.in(0))
  graph.in(1).connect(resolutionNode.in(1))

  const combineNode = graph.node<[BlurNoiseMetrics, ResolutionMetrics], [QualityMetrics]>({
    process: async (ctx) => {
      const bn: BlurNoiseMetrics = ctx.in(0)
      const res: ResolutionMetrics = ctx.in(1)
      const combined: QualityMetrics = {
        blurScore: bn.blurScore,
        noiseScore: bn.noiseScore,
        resolutionScore: res.resolutionScore,
        fullFaceVisible: res.fullFaceVisible,
      }
      ctx.out(0, combined)
    },
  })

  blurNoiseNode.out(0).connect(combineNode.in(0))
  resolutionNode.out(0).connect(combineNode.in(1))
  combineNode.out(0).connect(graph.out(0))

  return graph
}
